/**
 * App Catalogue — Distribution.
 *
 * CCMS mobile apps are distributed PRIVATELY: staff cannot find them by searching the App
 * Store or Play Store. Pick an app here to say how it actually reaches a phone.
 *
 *  - iOS     — Apple Business Manager custom-app redemption codes (a pool you upload and
 *              hand out), or an unlisted App Store / TestFlight link.
 *  - Android — an install link. Google Play has NO promo codes for free apps, so there is
 *              nothing to hand out: the user clicks a Play opt-in URL or downloads an APK.
 *
 * Route: /admin/app-catalogue/distribution
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconRefresh } from '@tabler/icons-react';
import { Stack, Group, Button, Text, Title, Alert, Loader } from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { useApplicationCatalogueService, useAppDistributionService } from '@actschurch/ccms-sdk';
import type { AppDistributionView } from '@actschurch/ccms-sdk';
import AppBrowser, { type AppBrowserItem } from './components/AppBrowser';

const APPS_KEY = ['admin', 'app-catalogue', 'all'];
const DISTRIBUTIONS_KEY = ['admin', 'app-catalogue', 'distributions'];

export default function DistributionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();
  const distributionService = useAppDistributionService();

  const { data: apps = [], isLoading: appsLoading } = useQuery({
    queryKey: APPS_KEY,
    queryFn: () => catalogueService.getAllApps(),
  });

  const {
    data: distributions = [],
    isLoading: distributionsLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: DISTRIBUTIONS_KEY,
    queryFn: () => distributionService.getDistributions(),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: DISTRIBUTIONS_KEY });
  };

  // Each tile says where the app stands, so the grid is worth scanning: is it set up, is
  // it live, and is its code pool running dry.
  const items: AppBrowserItem[] = useMemo(
    () =>
      apps.map((app) => {
        const mine = distributions.filter((d) => d.appId === app.appId);
        return {
          app,
          summary: summarise(mine),
          badge: badgeFor(mine),
        };
      }),
    [apps, distributions]
  );

  if (appsLoading || distributionsLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Distribution</Title>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </Stack>
    );
  }

  // A failing backend must NOT render as "nothing configured" — otherwise an un-run
  // migration looks exactly like a working-but-empty system.
  if (error) {
    return (
      <Stack gap="lg">
        <Title order={2}>Distribution</Title>
        <Alert color="red" title="Can't reach the distribution service">
          <Text size="sm">
            {error instanceof Error ? error.message : 'The Application Catalogue did not respond.'}
          </Text>
          <Text size="sm" mt="xs">
            If this is a fresh deploy: migration 194 creates these tables, and the
            <strong> ccms-api-application-catalogue</strong> container needs redeploying.
          </Text>
          <Button variant="light" color="red" size="xs" mt="sm" onClick={refresh}>
            Retry
          </Button>
        </Alert>
      </Stack>
    );
  }

  return (
    <FadeIn>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Distribution</Title>
            <Text c="dimmed" size="sm">
              How each app reaches a phone. CCMS apps are private — nobody can find them by
              searching the store.
            </Text>
          </div>
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconRefresh size={16} />}
            onClick={refresh}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Group>

        <AppBrowser
          items={items}
          onSelect={(appId) => navigate(`/admin/app-catalogue/distribution/${appId}`)}
          emptyMessage="No apps in the catalogue yet. Add an app manifest before configuring how it is distributed."
        />
      </Stack>
    </FadeIn>
  );
}

function summarise(distributions: AppDistributionView[]): string {
  if (distributions.length === 0) return 'No distribution set up yet';

  const parts = distributions.map((d) => {
    if (!d.yieldsCode) return `${d.platformName}: link`;
    const s = d.stats;
    if (!s) return `${d.platformName}: codes`;
    return `${d.platformName}: ${s.available} available · ${s.redeemed} used`;
  });
  return parts.join(' · ');
}

function badgeFor(distributions: AppDistributionView[]): { label: string; color: string } {
  if (distributions.length === 0) return { label: 'Not set up', color: 'gray' };

  // Running out of codes is the thing you most need to notice from a distance.
  if (distributions.some((d) => d.yieldsCode && d.stats?.isLowStock)) {
    return { label: 'Low on codes', color: 'red' };
  }
  if (distributions.every((d) => !d.enabled)) return { label: 'Hidden', color: 'gray' };
  return { label: 'Live', color: 'green' };
}
